<script lang="ts" setup>
import { ref, watch, nextTick } from 'vue'

import { EditorIcon } from '@/components/editor'
import type { MenuListProps } from '@/types'

const selectedCommandIndex = ref(0)
const selectedGroupIndex = ref(0)
const scrollContainer = ref<HTMLDivElement | null>(null)

const activeItemRefs = ref<(HTMLButtonElement | null)[]>([])
const props = withDefaults(defineProps<MenuListProps>(), {
  items: undefined,
  command: undefined,
})

defineExpose({ onKeyDown })

watch([() => selectedCommandIndex.value, () => selectedGroupIndex.value], async () => {
  if (!scrollContainer.value) return
  await nextTick()
  const activeItemIndex = selectedGroupIndex.value * 1000 + selectedCommandIndex.value
  const activeItem = activeItemRefs.value[activeItemIndex]

  if (activeItem) {
    activeItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }
})

function onKeyDown({ event }) {
  if (event.key === 'ArrowUp') {
    upHandler()
    return true
  }

  if (event.key === 'ArrowDown') {
    downHandler()
    return true
  }

  if (event.key === 'Enter') {
    enterHandler()
    return true
  }

  return false
}

function upHandler() {
  if (!props.items.length) {
    return false
  }
  let newCommandIndex = selectedCommandIndex.value - 1
  let newGroupIndex = selectedGroupIndex.value

  if (newCommandIndex < 0) {
    newGroupIndex = selectedGroupIndex.value - 1
    newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0
  }

  if (newGroupIndex < 0) {
    newGroupIndex = props.items.length - 1
    newCommandIndex = props.items[newGroupIndex].commands.length - 1
  }

  selectedCommandIndex.value = newCommandIndex
  selectedGroupIndex.value = newGroupIndex
}

function downHandler() {
  if (!props.items.length) {
    return false
  }
  const commands = props.items[selectedGroupIndex.value].commands
  let newCommandIndex = selectedCommandIndex.value + 1
  let newGroupIndex = selectedGroupIndex.value

  if (commands.length - 1 < newCommandIndex) {
    newCommandIndex = 0
    newGroupIndex = selectedGroupIndex.value + 1
  }
  if (props.items.length - 1 < newGroupIndex) {
    newGroupIndex = 0
  }
  selectedCommandIndex.value = newCommandIndex
  selectedGroupIndex.value = newGroupIndex
}

function enterHandler() {
  if (!props.items.length || selectedGroupIndex.value === -1 || selectedCommandIndex.value === -1) {
    return false
  }

  selectItem(selectedGroupIndex.value, selectedCommandIndex.value)
}

function selectItem(groupIndex: number, commandIndex: number) {
  const command = props.items[groupIndex].commands[commandIndex]
  props.command(command)
}

function createCommandClickHandler(groupIndex: number, commandIndex: number) {
  selectItem(groupIndex, commandIndex)
}
function setActiveItemRef(groupIndex: number, commandIndex: number, el: HTMLButtonElement | null) {
  activeItemRefs.value[groupIndex * 1000 + commandIndex] = el
}
</script>

<template>
  <div
    ref="scrollContainer"
    class="bg-background mb-8 max-h-[min(80vh,24rem)] flex-wrap overflow-auto rounded-lg border p-1 shadow-sm"
  >
    <div v-if="items?.length" class="grid min-w-48 grid-cols-1 gap-0.5">
      <template v-for="(group, groupIndex) in items" :key="group.title">
        <div
          class="col-[1/-1] mx-2 mt-2 text-[0.65rem] font-semibold tracking-wider uppercase select-none first:mt-0.5"
        >
          {{ group.title }}
        </div>
        <button
          v-for="(command, commandIndex) in group.commands"
          :ref="el => setActiveItemRef(groupIndex, commandIndex, el)"
          :key="commandIndex"
          class="flex w-full cursor-pointer items-center gap-3 rounded-sm px-2 py-1.5 text-left text-sm text-neutral-800 transition-colors outline-none dark:text-neutral-200"
          :class="[
            selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex
              ? 'bg-accent text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200'
              : 'hover:bg-accent hover:text-neutral-800 dark:hover:bg-neutral-900 dark:hover:text-neutral-200',
          ]"
          @click="createCommandClickHandler(groupIndex, commandIndex)"
        >
          <img v-if="command.iconUrl" class="h-6 w-6" :src="command.iconUrl" />
          <div v-else-if="command.iconClassName" :class="command.iconClassName" />
          <EditorIcon v-else-if="command.iconName" :name="command.iconName" class="mr-1 text-lg" />
          {{ command.label }}
        </button>
      </template>
    </div>
    <div v-else class="p-3">
      <span class="text-xs text-gray-800 dark:text-gray-100">No Result</span>
    </div>
  </div>
</template>
